import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useRef } from 'react';
import Verified from '@/assets/icons/Verified.svg';
import success from '../../../assets/images/downloadsuccess.png';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import AadharDetails from './AadharDetails';
import { useEffect } from 'react';

const DocumentCard = ({
  documentType,
  data,
  response,
  name,
  AadharApi,
  onPDFReady,
}) => {
  const isError = response && response.error;

  const pdfRef = useRef();
  const downloadBtnRef = useRef();
  const lastGeneratedDataRef = useRef(null); // Track the last data that was processed

  useEffect(() => {
    if (!data?.success || !pdfRef.current) {
      return;
    }

    const dataId = JSON.stringify({
      success: data.success,
      timestamp: data.timestamp,
      referenceId: data.referenceId,
      data: data.data,
    });

    // Check if we've already processed this exact data
    if (lastGeneratedDataRef.current === dataId) {
      return;
    }

    lastGeneratedDataRef.current = dataId;

    const generateAndSendPDF = async () => {
      try {
        const content = pdfRef.current.cloneNode(true);
        const downloadBtn = content.querySelector('#download-btn');
        if (downloadBtn) downloadBtn.style.display = 'none';

        const blob = await html2pdf()
          .from(content)
          .set({
            margin: [10, 10, 10, 10],
            filename: 'temp.pdf',
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: {
              unit: 'mm',
              format: 'a4',
              orientation: 'portrait',
            },
          })
          .outputPdf('blob');

        const file = new File([blob], `${documentType || 'response'}.pdf`, {
          type: 'application/pdf',
        });

        // 🔁 Call the callback with generated file
        if (typeof onPDFReady === 'function') {
          return onPDFReady(file);
        }
      } catch (err) {
        console.error('PDF generation failed', err);
        // Reset the ref if generation fails so it can be retried
        lastGeneratedDataRef.current = null;
      }
    };

    generateAndSendPDF();

    // Cleanup function to reset the flag when component unmounts or data changes
    return () => {
      // Don't reset here as we want to remember what we've processed
    };
  }, [
    data?.success,
    data?.timestamp,
    data?.referenceId,
    documentType,
    onPDFReady,
  ]); // More specific dependencies
  const successTypes = [
    'AADHAAR',
    'AADHAAR_TO_UAN',
    'AADHAAR_PAN_LINK_CHECK',
    'PAN_LITE',
    'PAN_TO_UAN',
    'PAN_COMPREHENSIVE',
    'DRIVING_LICENSE',
    'PASSPORT',
    'VOTER_ID',
    'DIN_VERIFICATION',
    'CREDIT_REPORT_V2',
    'FSSAI_VERIFICATION',
    'GST_VERIFICATION_ADVANCE',
    'PAN_ADVANCE_VERIFICATION',
    'PAN_UDYAM_MSME_STATUS',
    'CCRV_GENERATE_REQUEST',
    'CCRV_VERIFY_REQUEST',
  ];
  const images = successTypes.includes(documentType) ? success : Verified;

  const handleDownloadPDF = async () => {
    let isEmploymentHistory =
      documentType === 'EMPLOYMENT_HISTORY_UAN' ||
      documentType === 'EMPLOYMENT_HISTORY_ADVANCE' ||
      documentType === 'GENERATE_CCRV_REQUEST' ||
      documentType === 'EMPLOYMENT_HISTORY_WAGE_MONTH';

    try {
      if (!pdfRef.current) {
        console.error('PDF content not found.');
        return;
      }
      const content = pdfRef.current.cloneNode(true);
      const clonedButton = content.querySelector('#download-btn');
      if (clonedButton) clonedButton.style.display = 'none';
      const applyPageBreaks = () => {
        const elements = content.querySelectorAll('.page-break');
        elements.forEach((el) => {
          el.style.pageBreakAfter = 'always';
        });
      };

      applyPageBreaks();
      const filename =
        documentType === 'EMPLOYMENT_HISTORY_UAN'
          ? `${data?.data?.uan ? `${data.data.uan}_` : ''} ${documentType || 'response'}.pdf`
          : documentType === 'PAN_COMPREHENSIVE' || documentType === 'PAN_LITE'
            ? `${data?.data?.pan_number ? `${data.data.pan_number}_` : ''} "PAN_validation_report".pdf`
            : documentType === 'AADHAAR' || documentType === 'AADHAAR_TO_UAN'
              ? `${data?.data?.aadhaar_number ? `${data.data.aadhaar_number}_` : ''} ${documentType || 'response'}.pdf`
              : documentType === 'DRIVING_LICENSE'
                ? `${data?.data?.license_number ? `${data.data.license_number}_` : ''} ${documentType || 'response'}.pdf`
                : documentType === 'RC_MOBILE_NUMBER'
                  ? `${data?.data?.rc_number ? `${data.data.rc_number}_` : ''} ${documentType || 'response'}.pdf`
                  : documentType === 'AADHAAR_DETAILED_GENERATE_OTP'
                    ? `${data?.data?.maskedNumber ? `${data.data.maskedNumber}_` : ''} ${'AADHAAR_DETAILS'}.pdf`
                    : `${documentType || 'response'}.pdf`;

      await html2pdf()
        .from(content)
        .set({
          margin: [10, 10, 10, 10],
          filename: filename,
          html2canvas: { scale: 2, useCORS: true, letterRendering: true },
          jsPDF: {
            unit: 'mm',
            format: 'a4',
            orientation: isEmploymentHistory ? 'landscape' : 'portrait',
            compression: true,
            quality: 0.95,
          },
        })
        .save();
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };
  const renderData = (data) => {
    return (
      <div className="grid grid-cols-1 gap-y-2">
        {Object.entries(data).map(([key, value]) => {
          if (typeof value === 'boolean') {
            return null;
          }
          if (key === 'client_id') return null;
          const formattedKey = key
            ?.replace(/_/g, ' ')
            ?.replace(/\b\w/g, (char) => char?.toUpperCase());

          if (Array.isArray(value)) {
            if (value.every((item) => typeof item === 'string')) {
              return (
                <div key={key} className="grid grid-cols-1 gap-y-2 break-avoid">
                  <p className="text-muted-foreground font-bold mt-2">
                    {formattedKey}
                  </p>
                  <ul className="list-disc pl-4">
                    {value.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              );
            }

            return (
              <div key={key} className="grid grid-cols-1 gap-y-2">
                <p className="text-muted-foreground font-bold mt-2">
                  {formattedKey}
                </p>
                {value.map((item, index) => (
                  <div key={index} className="border-b p-2 mt-2">
                    {renderData(item)}
                  </div>
                ))}
              </div>
            );
          } else if (typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="grid grid-cols-1 gap-y-2">
                <p className="text-muted-foreground font-bold">
                  {formattedKey}
                </p>
                {renderData(value)}
              </div>
            );
          } else {
            return (
              <div key={key} className="grid grid-cols-2 gap-y-2 break-avoid">
                <p className="text-muted-foreground">{formattedKey}</p>
                <div className="flex gap-2 text-muted-foreground font-semibold">
                  :<p>{value || 'N/A'}</p>
                </div>
              </div>
            );
          }
        })}
      </div>
    );
  };

  return (
    <div ref={pdfRef} id="combined-content">
      <div className="rounded-lg border bg-background break-avoid">
        <div className="flex justify-between px-4 pt-3">
          <h2
            className={`${downloadBtnRef ? 'mb-2' : ''} text-lg font-semibold text-center`}
          >
            {name}
          </h2>
          {data?.success && (
            <button
              className="flex gap-2 justify-center items-center"
              style={{ touchAction: 'manipulation' }}
              onClick={handleDownloadPDF}
              ref={downloadBtnRef}
              id="download-btn"
            >
              <span className="max-sm:hidden">Download</span>
              <Download size={18} className="text-secondary-foreground" />
            </button>
          )}
        </div>
        {documentType === 'EMPLOYMENT_HISTORY_UAN' ||
        documentType === 'AADHAAR_DETAILED_GENERATE_OTP' ? (
          ''
        ) : (
          <div>
            <div className="border-t my-2 p-0"></div>
            <div className="flex flex-row justify-center px-4">
              <div className="flex flex-row gap-2">
                {data?.success && (
                  <div className="flex mt-3">
                    <p className="font-semibold text-sm text-muted-foreground">
                      Initiated At
                    </p>
                    <p className="text-muted-foreground text-sm">
                      :{' '}
                      {data?.timestamp
                        ? new Date(data?.timestamp).toLocaleString()
                        : 'N/A'}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div
              className="w-full m-auto max-w-[960px] p-4  overflow-auto flex flex-col items-center"
              id="response-content"
            >
              {data?.success && (
                <img src={images} alt={documentType} width={92} height={92} />
              )}
              {data?.success && (
                <span className="text-lg max-sm:text-sm pb-2.5 md:w-80 text-muted-foreground text-center">
                  {name} Verification Completed
                </span>
              )}
            </div>
          </div>
        )}
        {isError ? (
          <Card className="border-2 shadow-lg rounded-xl p-4 mt-2">
            <CardHeader>
              <CardTitle>{isError ? 'Error' : 'Response'}</CardTitle>
            </CardHeader>
            <CardContent>
              {isError ? (
                <div className="bg-red-100 text-red-700 p-3 rounded-md">
                  {response.error.message || 'An unknown error occurred.'}
                </div>
              ) : (
                <pre className="bg-gray-100 p-3 rounded-md overflow-auto">
                  {JSON.stringify(response, null, 2)}
                </pre>
              )}
            </CardContent>
          </Card>
        ) : (
          <div id="response-content" className="border-t">
            <CardContent className="mt-4">
              <div className="text-sm">
                {(() => {
                  switch (documentType) {
                    case 'CCRV_GENERATE_REQUEST':
                      return (
                        <div className="grid grid-cols-1 gap-y-4">
                          {/* Basic CCRV Information */}
                          <div className="grid grid-cols-1 gap-y-2">
                            <p className="text-muted-foreground font-bold text-lg">
                              CCRV Information
                            </p>

                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Transaction ID
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.transaction_id || 'N/A'}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                CCRV Status
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.ccrv_status || 'N/A'}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Case Count
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>
                                  {data?.data?.ccrv_data?.case_count || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* CCRV Cases Table */}
                          {data?.data?.ccrv_data?.cases &&
                            data.data.ccrv_data.cases.length > 0 && (
                              <div className="grid grid-cols-1 gap-y-2 mt-4">
                                <p className="text-muted-foreground font-bold text-lg">
                                  Case Details
                                </p>
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse">
                                    <thead>
                                      <tr className="bg-secondary">
                                        <th className="border p-2 text-left">
                                          Name
                                        </th>
                                        <th className="border p-2 text-left">
                                          Case Number
                                        </th>
                                        <th className="border p-2 text-left">
                                          CNR{' '}
                                        </th>
                                        <th className="border p-2 text-left">
                                          Case Type
                                        </th>
                                        <th className="border p-2 text-left">
                                          Case Status
                                        </th>
                                        <th className="border p-2 text-left">
                                          Case Year
                                        </th>
                                        <th className="border p-2 text-left">
                                          Risk Level
                                        </th>
                                        <th className="border p-2 text-left">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {data.data.ccrv_data.cases.map(
                                        (caseItem, index) => (
                                          <tr
                                            key={index}
                                            className={
                                              index % 2 === 0
                                                ? 'primary-foreground'
                                                : 'secondary'
                                            }
                                          >
                                            <td className="border p-2">
                                              {caseItem.name || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {caseItem.case_number || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {caseItem.cnr || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {caseItem.case_type || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {caseItem.case_status || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {caseItem.case_year || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {caseItem.algorithm_risk || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {caseItem?.link ? (
                                                <a
                                                  href={caseItem.link}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-blue-600 hover:underline"
                                                >
                                                  View Details
                                                </a>
                                              ) : (
                                                'NA'
                                              )}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                          {/* Case Details Cards */}
                          {data?.data?.ccrv_data?.cases &&
                            data.data.ccrv_data.cases.length > 0 && (
                              <div className="grid grid-cols-1 gap-y-4 mt-4">
                                <p className="text-muted-foreground font-bold text-lg">
                                  Detailed Case Information
                                </p>
                                {data.data.ccrv_data.cases.map(
                                  (caseItem, index) => (
                                    <div
                                      key={index}
                                      className="border p-4 rounded-md shadow-sm"
                                    >
                                      <div className="flex justify-between mb-2">
                                        <p className="font-bold text-md">
                                          {caseItem.name || 'N/A'}
                                        </p>
                                        <p className="text-sm bg-secondary px-2 py-1 rounded-md">
                                          {caseItem.case_status || 'N/A'}
                                        </p>
                                      </div>

                                      <div className="grid grid-cols-2 gap-y-2 md:grid-cols-3">
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            Case Type
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.case_type || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            Case Number
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.case_number || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            CNR
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.cnr || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            Risk Level
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.algorithm_risk || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            Filing Date
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.filing_date || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            Decision Date
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.decision_date || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            Match Type
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.name_match_type || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            State
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.state_name || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            District
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.district_name || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground text-sm">
                                            Case Category
                                          </p>
                                          <p className="font-semibold">
                                            {caseItem.case_category || 'N/A'}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="mt-3">
                                        <p className="text-muted-foreground text-sm">
                                          Under Acts
                                        </p>
                                        <p className="font-semibold">
                                          {caseItem.under_acts || 'N/A'}
                                        </p>
                                      </div>

                                      <div className="mt-3">
                                        <p className="text-muted-foreground text-sm">
                                          Under Sections
                                        </p>
                                        <p className="font-semibold">
                                          {caseItem.under_sections || 'N/A'}
                                        </p>
                                      </div>

                                      <div className="mt-3">
                                        <p className="text-muted-foreground text-sm">
                                          Opposing Party
                                        </p>
                                        <p className="font-semibold">
                                          {caseItem.oparty || 'N/A'}
                                        </p>
                                      </div>

                                      {caseItem?.link && (
                                        <div className="mt-4 flex justify-end">
                                          <a
                                            href={caseItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                          >
                                            View Full Case
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                        </div>
                      );

                    case 'VOTER_ID':
                      return (
                        <div className="mt-4">
                          <TableRowForVoterId
                            label="Name"
                            value={data?.data?.name}
                          />
                          <TableRowForVoterId
                            label="Relative's Name"
                            value={data?.data?.relation_name}
                          />
                          <TableRowForVoterId
                            label="Relation's Type"
                            value={data?.data?.relation_type}
                          />
                          <TableRowForVoterId
                            label="Age"
                            value={data?.data?.age}
                          />
                          <TableRowForVoterId
                            label="Gender"
                            value={data?.data?.gender}
                          />
                          <TableRowForVoterId
                            label="EPIC No"
                            value={data?.data?.epic_no}
                          />
                          <TableRowForVoterId
                            label="Date of Birth"
                            value={data?.data?.dob}
                          />
                          <TableRowForVoterId
                            label="Area"
                            value={data?.data?.area}
                          />
                          <TableRowForVoterId
                            label="State"
                            value={data?.data?.state}
                          />
                          <TableRowForVoterId
                            label="House No."
                            value={data?.data?.house_no}
                          />
                        </div>
                      );
                    case 'AADHAAR':
                    case 'PAN_COMPREHENSIVE':
                    case 'RCTEXT':
                    case 'DRIVING_LICENSE':
                    case 'PAN_LITE':
                      return (
                        <div className="grid grid-cols-1 gap-y-2">
                          {Object.entries(data?.data).map(([key, value]) => {
                            if (typeof value === 'boolean') {
                              return null;
                            }
                            if (key === 'client_id' || key === 'remarks')
                              return null;

                            if (
                              value &&
                              typeof value === 'object' &&
                              !Array.isArray(value)
                            ) {
                              return (
                                <div
                                  key={key}
                                  className="grid grid-cols-1 gap-y-2"
                                >
                                  <p className="text-muted-foreground font-bold">
                                    {key
                                      .replace(/_/g, ' ')
                                      .replace(/\b\w/g, (char) =>
                                        char.toUpperCase(),
                                      )}
                                  </p>
                                  <div>
                                    {Object.entries(value).map(
                                      ([subKey, subValue]) => (
                                        <div
                                          key={subKey}
                                          className="grid grid-cols-2 gap-y-2"
                                        >
                                          <p className="text-muted-foreground">
                                            {subKey
                                              ?.replace(/_/g, ' ')
                                              ?.replace(/\b\w/g, (char) =>
                                                char?.toUpperCase(),
                                              )}
                                          </p>
                                          <div className="flex gap-2 text-muted-foreground font-semibold">
                                            :<p>{subValue || 'N/A'}</p>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={key}
                                className="grid grid-cols-2 gap-y-2"
                              >
                                <p className="text-muted-foreground">
                                  {key
                                    ?.replace(/_/g, ' ')
                                    ?.replace(/\b\w/g, (char) =>
                                      char?.toUpperCase(),
                                    )}
                                </p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{value || 'N/A'}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    case 'DIRECTOR_PHONE':
                    case 'ELECTRICITY_BILL_DETAILS':
                    case 'FASTAG_RC':
                    case 'FASTAG_VERIFICATION':
                    case 'FIND_UPI_ID':
                    case 'MOBILE_TO_RC':
                    case 'MOBILE_TO_BANK':
                    case 'MOBILE_TO_UAN':
                    case 'PAN_TO_UAN':
                    case 'PASSPORT':
                    case 'RC_MOBILE_NUMBER':
                    case 'TAN':
                    case 'TELECOM_VERIFICATION':
                    case 'TIN_VERIFICATION':
                    case 'AADHAAR_PAN_LINK_CHECK':
                    case 'AADHAAR_TO_UAN':
                      return (
                        <div className="grid grid-cols-1 gap-y-2">
                          {Object.entries(data?.data).map(([key, value]) => {
                            if (key === 'client_id' || key === 'remarks')
                              return null;

                            if (typeof value === 'boolean') {
                              return null;
                            }
                            return (
                              <div
                                key={key}
                                className="grid grid-cols-2 gap-y-2"
                              >
                                <p className=" text-muted-foreground">
                                  {key
                                    ?.replace(/_/g, ' ')
                                    ?.replace(/\b\w/g, (char) =>
                                      char?.toUpperCase(),
                                    )}{' '}
                                </p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{value || 'N/A'}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    case 'AADHAAR_DETAILED_GENERATE_OTP':
                      return (
                        <AadharDetails data={data} AadharApi={AadharApi} />
                      );
                    case 'BANK_VERIFICATION':
                    case 'BANK_VERIFICATION_PENNYLESS':
                      return (
                        <div className="grid grid-cols-1 gap-y-2">
                          {Object.entries(data?.data).map(([key, value]) => {
                            if (typeof value === 'boolean') {
                              return null;
                            }
                            if (key === 'client_id' || key === 'remarks')
                              return null;

                            if (typeof value === 'object' && value !== null) {
                              return (
                                <div
                                  key={key}
                                  className="grid grid-cols-1 gap-y-2"
                                >
                                  <p className=" text-muted-foreground">
                                    {key
                                      ?.replace(/_/g, ' ')
                                      ?.replace(/\b\w/g, (char) =>
                                        char?.toUpperCase(),
                                      )}
                                  </p>
                                  {Object.entries(value).map(
                                    ([subKey, subValue]) => (
                                      <div
                                        key={subKey}
                                        className="grid grid-cols-2"
                                      >
                                        <p className="text-muted-foreground">
                                          {subKey
                                            ?.replace(/_/g, ' ')
                                            ?.replace(/\b\w/g, (char) =>
                                              char?.toUpperCase(),
                                            )}
                                        </p>
                                        <div className="flex gap-2 text-muted-foreground font-semibold">
                                          :
                                          <p>
                                            {typeof subValue === 'boolean'
                                              ? subValue
                                                ? 'Yes'
                                                : 'No'
                                              : subValue || 'N/A'}
                                          </p>
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>
                              );
                            }

                            return (
                              <div
                                key={key}
                                className="grid grid-cols-2 gap-y-2"
                              >
                                <p className="font-semibold text-muted-foreground">
                                  {key
                                    ?.replace(/_/g, ' ')
                                    ?.replace(/\b\w/g, (char) =>
                                      char?.toUpperCase(),
                                    )}
                                </p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{value || 'N/A'}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    case 'CORPORATE_CIN':
                    case 'CORPORATE_GSTIN':
                    case 'CREDIT_REPORT':
                    case 'ECOURT_CNR':
                    case 'RATION_CARD':
                    case 'TAN_COMPANY_SEARCH':
                    case 'UDYOG_AADHAAR':
                    case 'ECHALLAN':
                      return renderData(data?.data);
                    case 'EMPLOYMENT_HISTORY_UAN':
                      return (
                        <div className="mt-5">
                          <Table className="overflow-y-auto mt-7">
                            <TableHeader className="bg-secondary">
                              <TableHead>Name</TableHead>
                              <TableHead>Guardian Name</TableHead>
                              <TableHead>Establishment</TableHead>
                              <TableHead>Member ID</TableHead>
                              <TableHead>Joining Date</TableHead>
                              <TableHead>Wage Month</TableHead>
                              <TableHead>Exit Date</TableHead>
                            </TableHeader>
                            {Array.isArray(data?.data?.employment_history) &&
                              data?.data?.employment_history.map(
                                (item, index) => (
                                  <TableRow
                                    key={index}
                                    className="overflow-y-auto"
                                  >
                                    <TableCell className="whitespace-normal break-words max-w-[160px]">
                                      {item.name || 'N/A'}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words max-w-[160px]">
                                      {item.guardian_name || 'N/A'}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words max-w-[130px]">
                                      {item.establishment_name || 'N/A'}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words max-w-[130px]">
                                      {item.member_id || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item.date_of_joining || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item.wage_month || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item.date_of_exit || 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                          </Table>
                        </div>
                      );
                    case 'EMPLOYMENT_HISTORY_ADVANCE':
                      return (
                        <div className="mt-5">
                          <Table className="overflow-y-auto mt-7">
                            <TableHeader className="bg-secondary">
                              <TableHead>Name</TableHead>
                              <TableHead>Guardian Name</TableHead>
                              <TableHead>Establishment</TableHead>
                              <TableHead>Member ID</TableHead>
                              <TableHead>Joining Date</TableHead>
                              <TableHead>Wage Month</TableHead>
                              <TableHead>Exit Date</TableHead>
                            </TableHeader>
                            {Array.isArray(data?.data?.employment_data) &&
                              data?.data?.employment_data.map((item, index) => (
                                <TableRow
                                  key={index}
                                  className="overflow-y-auto"
                                >
                                  <TableCell className="whitespace-normal break-words max-w-[160px]">
                                    {item.name || 'N/A'}
                                  </TableCell>
                                  <TableCell className="whitespace-normal break-words max-w-[160px]">
                                    {item.guardian_name || 'N/A'}
                                  </TableCell>
                                  <TableCell className="whitespace-normal break-words max-w-[130px]">
                                    {item.establishment_name || 'N/A'}
                                  </TableCell>
                                  <TableCell className="whitespace-normal break-words max-w-[130px]">
                                    {item.member_id || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {item.date_of_joining || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {item.wage_month || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {item.date_of_exit || 'N/A'}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </Table>
                        </div>
                      );
                    case 'EMPLOYMENT_HISTORY_WAGE_MONTH':
                      return (
                        <div className="mt-5">
                          <Table className="overflow-y-auto mt-7">
                            <TableHeader className="bg-secondary">
                              <TableHead>Name</TableHead>
                              <TableHead>Guardian Name</TableHead>
                              <TableHead>Establishment</TableHead>
                              <TableHead>Member ID</TableHead>
                              <TableHead>Joining Date</TableHead>
                              <TableHead>Wage Month</TableHead>
                              <TableHead>Exit Date</TableHead>
                            </TableHeader>
                            {Array.isArray(data?.data?.employment_history) &&
                              data?.data?.employment_history.map(
                                (item, index) => (
                                  <TableRow
                                    key={index}
                                    className="overflow-y-auto"
                                  >
                                    <TableCell className="whitespace-normal break-words max-w-[160px]">
                                      {item.name || 'N/A'}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words max-w-[160px]">
                                      {item.guardian_name || 'N/A'}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words max-w-[130px]">
                                      {item.establishment_name || 'N/A'}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words max-w-[130px]">
                                      {item.member_id || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item.date_of_joining || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item.wage_month || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item.date_of_exit || 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                          </Table>
                        </div>
                      );
                    case 'EMPLOYMENT_COMPOSITE_API':
                      return (
                        <div className="mt-5">
                          <Table className="overflow-y-auto mt-7">
                            <TableHeader className="bg-secondary">
                              <TableHead>Name</TableHead>
                              <TableHead>Guardian Name</TableHead>
                              <TableHead>Establishment</TableHead>
                              <TableHead>Member ID</TableHead>
                              <TableHead>Joining Date</TableHead>
                              <TableHead>Wage Month</TableHead>
                              <TableHead>Exit Date</TableHead>
                            </TableHeader>
                            {Array.isArray(data?.data?.employment_history) &&
                              data?.data?.employment_history.map(
                                (item, index) => (
                                  <TableRow
                                    key={index}
                                    className="overflow-y-auto"
                                  >
                                    <TableCell className="whitespace-normal break-words max-w-[160px]">
                                      {item.name || 'N/A'}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words max-w-[160px]">
                                      {item.guardian_name || 'N/A'}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words max-w-[130px]">
                                      {item.establishment_name || 'N/A'}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words max-w-[130px]">
                                      {item.member_id || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item.date_of_joining || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item.wage_month || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {item.date_of_exit || 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                          </Table>
                        </div>
                      );
                    case 'FSSAI_VERIFICATION':
                      return (
                        <div className="grid grid-cols-1 gap-y-2">
                          {/* Basic FSSAI Information */}
                          <div className="grid grid-cols-2 gap-y-2">
                            <p className="text-muted-foreground">
                              FSSAI Number
                            </p>
                            <div className="flex gap-2 text-muted-foreground font-semibold">
                              :<p>{data?.data?.fssai_number || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-y-2">
                            <p className="text-muted-foreground">ID Number</p>
                            <div className="flex gap-2 text-muted-foreground font-semibold">
                              :<p>{data?.data?.id_number || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-y-2">
                            <p className="text-muted-foreground">
                              Application Number
                            </p>
                            <div className="flex gap-2 text-muted-foreground font-semibold">
                              :<p>{data?.data?.application_number || 'N/A'}</p>
                            </div>
                          </div>

                          {/* FSSAI Details Section */}
                          {data?.data?.details &&
                            data.data.details.length > 0 && (
                              <div className="mt-4">
                                <p className="text-muted-foreground font-bold mb-2">
                                  FSSAI Details
                                </p>
                                {data.data.details.map((detail, index) => (
                                  <div
                                    key={index}
                                    className="border p-3 rounded-md mb-3"
                                  >
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        Company Name
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :<p>{detail.company_name || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        Status
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :<p>{detail.status_desc || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        License Category
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :
                                        <p>
                                          {detail.license_category_name ||
                                            'N/A'}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        Address
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :
                                        <p>
                                          {detail.address_premises || 'N/A'}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        State
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :<p>{detail.state_name || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        District
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :<p>{detail.district_name || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        Taluk
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :<p>{detail.taluk_name || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        PIN Code
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :<p>{detail.pincode || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        Application Type
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :<p>{detail.app_type_desc || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        Application Date
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :
                                        <p>
                                          {detail.app_submission_date || 'N/A'}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        Last Updated
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :
                                        <p>{detail.last_updated_on || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        Reference ID
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :<p>{detail.ref_id || 'N/A'}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-2">
                                      <p className="text-muted-foreground">
                                        FBO ID
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :<p>{detail.fbo_id || 'N/A'}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      );
                    case 'GST_VERIFICATION_ADVANCE':
                      return (
                        <div className="grid grid-cols-1 gap-y-4">
                          {/* Basic GST Information */}
                          <div className="grid grid-cols-1 gap-y-2">
                            <p className="text-muted-foreground font-bold text-lg">
                              Basic Information
                            </p>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">GSTIN</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.gstin || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                PAN Number
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.pan_number || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Legal Name
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.legal_name || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Business Name
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.business_name || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Constitution of Business
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>
                                  {data?.data?.constitution_of_business ||
                                    'N/A'}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Taxpayer Type
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.taxpayer_type || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                GSTIN Status
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.gstin_status || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Registration Date
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>
                                  {data?.data?.date_of_registration || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Business Activity */}
                          <div className="grid grid-cols-1 gap-y-2 mt-4">
                            <p className="text-muted-foreground font-bold text-lg">
                              Business Activity
                            </p>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Annual Turnover
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>
                                  {data?.data?.annual_turnover || 'N/A'} (
                                  {data?.data?.annual_turnover_fy || 'N/A'})
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Percentage in Cash
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>
                                  {data?.data?.percentage_in_cash || 'N/A'} (
                                  {data?.data?.percentage_in_cash_fy || 'N/A'})
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Core Business Activity
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>
                                  {data?.data
                                    ?.nature_of_core_business_activity_description ||
                                    'N/A'}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Nature of Business Activities
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <div>
                                  {data?.data?.nature_bus_activities &&
                                  Array.isArray(
                                    data?.data?.nature_bus_activities,
                                  ) ? (
                                    <ul className="list-disc pl-4">
                                      {data.data.nature_bus_activities.map(
                                        (activity, index) => (
                                          <li key={index}>{activity}</li>
                                        ),
                                      )}
                                    </ul>
                                  ) : (
                                    'N/A'
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Jurisdiction */}
                          <div className="grid grid-cols-1 gap-y-2 mt-4">
                            <p className="text-muted-foreground font-bold text-lg">
                              Jurisdiction
                            </p>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Center Jurisdiction
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>
                                  {data?.data?.center_jurisdiction || 'N/A'}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                State Jurisdiction
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>{data?.data?.state_jurisdiction || 'N/A'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Contact Details */}
                          {data?.data?.contact_details && (
                            <div className="grid grid-cols-1 gap-y-2 mt-4">
                              <p className="text-muted-foreground font-bold text-lg">
                                Contact Details
                              </p>

                              {/* Principal Contact */}
                              {data.data.contact_details.principal && (
                                <div className="border p-3 rounded-md mb-3">
                                  <p className="text-muted-foreground font-semibold mb-2">
                                    Principal Address
                                  </p>
                                  <div className="grid grid-cols-2 gap-y-2">
                                    <p className="text-muted-foreground">
                                      Address
                                    </p>
                                    <div className="flex gap-2 text-muted-foreground font-semibold">
                                      :
                                      <p>
                                        {data.data.contact_details.principal
                                          .address || 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-y-2">
                                    <p className="text-muted-foreground">
                                      Email
                                    </p>
                                    <div className="flex gap-2 text-muted-foreground font-semibold">
                                      :
                                      <p>
                                        {data.data.contact_details.principal
                                          .email || 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-y-2">
                                    <p className="text-muted-foreground">
                                      Mobile
                                    </p>
                                    <div className="flex gap-2 text-muted-foreground font-semibold">
                                      :
                                      <p>
                                        {data.data.contact_details.principal
                                          .mobile || 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-y-2">
                                    <p className="text-muted-foreground">
                                      Nature of Business
                                    </p>
                                    <div className="flex gap-2 text-muted-foreground font-semibold">
                                      :
                                      <p>
                                        {data.data.contact_details.principal
                                          .nature_of_business || 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Additional Addresses */}
                              {data.data.contact_details.additional &&
                                data.data.contact_details.additional.length >
                                  0 && (
                                  <div>
                                    <p className="text-muted-foreground font-semibold mb-2">
                                      Additional Addresses
                                    </p>
                                    {data.data.contact_details.additional.map(
                                      (address, index) => (
                                        <div
                                          key={index}
                                          className="border p-3 rounded-md mb-3"
                                        >
                                          <p className="text-muted-foreground font-semibold mb-2">
                                            Address {index + 1}
                                          </p>
                                          <div className="grid grid-cols-2 gap-y-2">
                                            <p className="text-muted-foreground">
                                              Address
                                            </p>
                                            <div className="flex gap-2 text-muted-foreground font-semibold">
                                              :<p>{address.address || 'N/A'}</p>
                                            </div>
                                          </div>
                                          <div className="grid grid-cols-2 gap-y-2">
                                            <p className="text-muted-foreground">
                                              Email
                                            </p>
                                            <div className="flex gap-2 text-muted-foreground font-semibold">
                                              :<p>{address.email || 'N/A'}</p>
                                            </div>
                                          </div>
                                          <div className="grid grid-cols-2 gap-y-2">
                                            <p className="text-muted-foreground">
                                              Mobile
                                            </p>
                                            <div className="flex gap-2 text-muted-foreground font-semibold">
                                              :<p>{address.mobile || 'N/A'}</p>
                                            </div>
                                          </div>
                                          <div className="grid grid-cols-2 gap-y-2">
                                            <p className="text-muted-foreground">
                                              Nature of Business
                                            </p>
                                            <div className="flex gap-2 text-muted-foreground font-semibold">
                                              :
                                              <p>
                                                {address.nature_of_business ||
                                                  'N/A'}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                            </div>
                          )}

                          {/* Promoters */}
                          {data?.data?.promoters &&
                            data.data.promoters.length > 0 && (
                              <div className="grid grid-cols-1 gap-y-2 mt-4">
                                <p className="text-muted-foreground font-bold text-lg">
                                  Promoters
                                </p>
                                <ul className="list-disc pl-4">
                                  {data.data.promoters.map(
                                    (promoter, index) => (
                                      <li
                                        key={index}
                                        className="text-muted-foreground"
                                      >
                                        {promoter}
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            )}

                          {/* Filing Status */}
                          {data?.data?.filing_status &&
                            data.data.filing_status.length > 0 &&
                            Array.isArray(data.data.filing_status[0]) && (
                              <div className="grid grid-cols-1 gap-y-2 mt-4">
                                <p className="text-muted-foreground font-bold text-lg">
                                  Filing Status
                                </p>
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse">
                                    <thead>
                                      <tr className="bg-secondary">
                                        <th className="border p-2 text-left">
                                          Return Type
                                        </th>
                                        <th className="border p-2 text-left">
                                          Financial Year
                                        </th>
                                        <th className="border p-2 text-left">
                                          Tax Period
                                        </th>
                                        <th className="border p-2 text-left">
                                          Date of Filing
                                        </th>
                                        <th className="border p-2 text-left">
                                          Status
                                        </th>
                                        <th className="border p-2 text-left">
                                          Mode of Filing
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {data.data.filing_status[0].map(
                                        (filing, index) => (
                                          <tr
                                            key={index}
                                            className={
                                              index % 2 === 0
                                                ? 'primary-foreground'
                                                : 'secondary'
                                            }
                                          >
                                            <td className="border p-2">
                                              {filing.return_type || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {filing.financial_year || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {filing.tax_period || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {filing.date_of_filing || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {filing.status || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {filing.mode_of_filing || 'N/A'}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                        </div>
                      );
                    case 'CREDIT_REPORT_PDF':
                      return (
                        <div className="grid grid-cols-1 gap-y-2">
                          {data?.data &&
                            Object.entries(data.data).map(([key, value]) => {
                              if (
                                typeof value === 'boolean' ||
                                key === 'client_id' ||
                                key === 'credit_report_link'
                              ) {
                                return null;
                              }

                              return (
                                <div
                                  key={key}
                                  className="grid grid-cols-2 gap-y-2"
                                >
                                  <p className="text-muted-foreground">
                                    {key
                                      .replace(/_/g, ' ')
                                      .replace(/\b\w/g, (char) =>
                                        char.toUpperCase(),
                                      )}
                                  </p>
                                  <div className="flex gap-2 text-muted-foreground font-semibold">
                                    :
                                    <p>
                                      {typeof value === 'object'
                                        ? JSON.stringify(value)
                                        : value || 'N/A'}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}

                          {data?.data?.credit_report &&
                            Object.keys(data.data.credit_report).length > 0 && (
                              <div className="grid grid-cols-1 gap-y-2">
                                <p className="text-muted-foreground font-semibold">
                                  Credit Report
                                </p>
                                {Object.entries(data.data.credit_report).map(
                                  ([key, value]) => (
                                    <div
                                      key={key}
                                      className="grid grid-cols-2 gap-y-2"
                                    >
                                      <p className="text-muted-foreground">
                                        {key
                                          .replace(/_/g, ' ')
                                          .replace(/\b\w/g, (char) =>
                                            char.toUpperCase(),
                                          )}
                                      </p>
                                      <div className="flex gap-2 text-muted-foreground font-semibold">
                                        :
                                        {typeof value === 'object' ? (
                                          <pre className="text-xs">
                                            {JSON.stringify(value, null, 2)}
                                          </pre>
                                        ) : (
                                          <p>{value || 'N/A'}</p>
                                        )}
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            )}

                          {data?.data?.credit_report_link && (
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Credit Report Link
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <a
                                  href={data.data.credit_report_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 underline"
                                >
                                  View Report
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    case 'CREDIT_REPORT_V2':
                      return data?.data?.sub_code ===
                        'NO_CREDIT_REPORT_FOUND' ? (
                        <div className="grid grid-cols-1 gap-y-2">
                          <div className="grid grid-cols-2 gap-y-2">
                            <p className="text-muted-foreground">Status</p>
                            <div className="flex gap-2 text-muted-foreground font-semibold">
                              :<p className="">NO RECORD FOUND</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-y-2">
                          {/* Static data for Credit Report */}
                          <div className="grid grid-cols-1 gap-y-2">
                            <p className="text-muted-foreground font-semibold">
                              Credit Report
                            </p>

                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">Status</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.credit_score}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">Mobile</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.mobile}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">Name</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.name || 'N/A'}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">PAN</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.pan || 'N/A'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Download Link */}
                          <div className="grid grid-cols-2 gap-y-2 mt-2">
                            <p className="text-muted-foreground">
                              Credit Report Link
                            </p>
                            <div className="flex gap-2 text-muted-foreground font-semibold">
                              :
                              {data?.data?.pdf_url ? (
                                <a
                                  href={data.data.pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 underline"
                                >
                                  Download Report
                                </a>
                              ) : (
                                <span className="text-blue-500 cursor-pointer underline">
                                  Download Report
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    case 'DIN_VERIFICATION':
                      return (
                        <div className="grid grid-cols-1 gap-y-4">
                          {/* Director Information Section */}
                          <div className="grid grid-cols-1 gap-y-2">
                            <p className="text-muted-foreground font-bold text-lg">
                              Director Information
                            </p>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">DIN</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>{data?.data?.director_data?.din || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">Name</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>
                                  {data?.data?.director_data?.name || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Company Data Section */}
                          {data?.data?.company_data &&
                            data.data.company_data.length > 0 && (
                              <div className="grid grid-cols-1 gap-y-2">
                                <p className="text-muted-foreground font-bold text-lg mt-4">
                                  Company Associations
                                </p>
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse">
                                    <thead>
                                      <tr className="bg-secondary">
                                        <th className="border p-2 text-left">
                                          Company Name
                                        </th>
                                        <th className="border p-2 text-left">
                                          Designation
                                        </th>
                                        <th className="border p-2 text-left">
                                          CIN/FCRN
                                        </th>
                                        <th className="border p-2 text-left">
                                          Begin Date
                                        </th>
                                        <th className="border p-2 text-left">
                                          End Date
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {data.data.company_data.map(
                                        (company, index) => (
                                          <tr
                                            key={index}
                                            className={
                                              index % 2 === 0
                                                ? 'primary-foreground'
                                                : 'secondary'
                                            }
                                          >
                                            <td className="border p-2 whitespace-normal break-words max-w-[200px]">
                                              {company.company_name || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {company.designation || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {company.cin_fcrn ||
                                                company['cin/fcrn'] ||
                                                'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {company.begin_date || 'N/A'}
                                            </td>
                                            <td className="border p-2">
                                              {company.end_date || 'N/A'}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                          {/* LLP Data Section */}
                          {data?.data?.llp_data &&
                            data.data.llp_data.length > 0 && (
                              <div className="grid grid-cols-1 gap-y-2">
                                <p className="text-muted-foreground font-bold text-lg mt-4">
                                  LLP Associations
                                </p>
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse">
                                    <thead>
                                      <tr className="bg-secondary">
                                        <th className="border p-2 text-left">
                                          LLP Name
                                        </th>
                                        <th className="border p-2 text-left">
                                          Designation
                                        </th>
                                        <th className="border p-2 text-left">
                                          LLPIN/FLLPIN
                                        </th>
                                        <th className="border p-2 text-left">
                                          Begin Date
                                        </th>
                                        <th className="border p-2 text-left">
                                          End Date
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {data.data.llp_data.map((llp, index) => (
                                        <tr
                                          key={index}
                                          className={
                                            index % 2 === 0
                                              ? 'bg-white'
                                              : 'bg-gray-50'
                                          }
                                        >
                                          <td className="border p-2 whitespace-normal break-words max-w-[200px]">
                                            {llp.llp_name || 'N/A'}
                                          </td>
                                          <td className="border p-2">
                                            {llp.designation || 'N/A'}
                                          </td>
                                          <td className="border p-2">
                                            {llp.llpin_fllpin ||
                                              llp['llpin/fllpin'] ||
                                              'N/A'}
                                          </td>
                                          <td className="border p-2">
                                            {llp.begin_date || 'N/A'}
                                          </td>
                                          <td className="border p-2">
                                            {llp.end_date || 'N/A'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                        </div>
                      );
                    case 'TAN_VERIFICATION':
                      return (
                        <div className="grid grid-cols-1 gap-y-2">
                          {Object.entries(data?.data).map(([key, value]) => {
                            if (typeof value === 'boolean') {
                              return null;
                            }
                            if (key === 'client_id' || key === 'remarks')
                              return null;

                            // Format date fields if they look like timestamps
                            let displayValue = value;
                            if (
                              key === 'dtTanAllotment' &&
                              typeof value === 'number'
                            ) {
                              displayValue = new Date(value).toLocaleDateString(
                                'en-GB',
                              );
                            }

                            return (
                              <div
                                key={key}
                                className="grid grid-cols-2 gap-y-2"
                              >
                                <p className="text-muted-foreground">
                                  {key
                                    ?.replace(/_/g, ' ')
                                    ?.replace(/\b\w/g, (char) =>
                                      char?.toUpperCase(),
                                    )}
                                </p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{displayValue || 'N/A'}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    case 'PAN_ADVANCE_VERIFICATION':
                      return (
                        <div className="grid grid-cols-1 gap-y-4">
                          {/* Basic PAN Information */}
                          <div className="grid grid-cols-1 gap-y-2">
                            <p className="text-muted-foreground font-bold text-lg">
                              Basic Information
                            </p>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                PAN Number
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.pan_number || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">Full Name</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.full_name || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">Gender</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.gender || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Date of Birth
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.dob || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">Category</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.category || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Aadhaar Linked
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :
                                <p>
                                  {data?.data?.aadhaar_linked ? 'Yes' : 'No'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Contact Information */}
                          <div className="grid grid-cols-1 gap-y-2 mt-4">
                            <p className="text-muted-foreground font-bold text-lg">
                              Contact Information
                            </p>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Masked Aadhaar
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.masked_aadhaar || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">Email</p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.email || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-2">
                              <p className="text-muted-foreground">
                                Phone Number
                              </p>
                              <div className="flex gap-2 text-muted-foreground font-semibold">
                                :<p>{data?.data?.phone_number || 'N/A'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Address Information */}
                          {data?.data?.address && (
                            <div className="grid grid-cols-1 gap-y-2 mt-4">
                              <p className="text-muted-foreground font-bold text-lg">
                                Address
                              </p>
                              <div className="grid grid-cols-2 gap-y-2">
                                <p className="text-muted-foreground">
                                  Full Address
                                </p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{data?.data?.address?.full || 'N/A'}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-y-2">
                                <p className="text-muted-foreground">Line 1</p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{data?.data?.address?.line_1 || 'N/A'}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-y-2">
                                <p className="text-muted-foreground">Line 2</p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{data?.data?.address?.line_2 || 'N/A'}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-y-2">
                                <p className="text-muted-foreground">
                                  Street Name
                                </p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :
                                  <p>
                                    {data?.data?.address?.street_name || 'N/A'}
                                  </p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-y-2">
                                <p className="text-muted-foreground">City</p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{data?.data?.address?.city || 'N/A'}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-y-2">
                                <p className="text-muted-foreground">State</p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{data?.data?.address?.state || 'N/A'}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-y-2">
                                <p className="text-muted-foreground">
                                  ZIP/Postal Code
                                </p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{data?.data?.address?.zip || 'N/A'}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-y-2">
                                <p className="text-muted-foreground">Country</p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :
                                  <p>{data?.data?.address?.country || 'N/A'}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    case 'PAN_UDYAM_MSME_STATUS':
                      return (
                        <div className="grid grid-cols-1 gap-y-2">
                          {Object.entries(data?.data).map(([key, value]) => {
                            if (typeof value === 'boolean') {
                              // Display boolean values for this specific document type
                              if (key === 'udyam_exists') {
                                return (
                                  <div
                                    key={key}
                                    className="grid grid-cols-2 gap-y-2"
                                  >
                                    <p className="text-muted-foreground">
                                      UDYAM Registered
                                    </p>
                                    <div className="flex gap-2 text-muted-foreground font-semibold">
                                      :<p>{value ? 'Yes' : 'No'}</p>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }
                            if (key === 'client_id' || key === 'remarks')
                              return null;

                            if (
                              value &&
                              typeof value === 'object' &&
                              !Array.isArray(value)
                            ) {
                              return (
                                <div
                                  key={key}
                                  className="grid grid-cols-1 gap-y-2"
                                >
                                  <p className="text-muted-foreground font-bold">
                                    {key
                                      .replace(/_/g, ' ')
                                      .replace(/\b\w/g, (char) =>
                                        char.toUpperCase(),
                                      )}
                                  </p>
                                  <div>
                                    {Object.entries(value).map(
                                      ([subKey, subValue]) => (
                                        <div
                                          key={subKey}
                                          className="grid grid-cols-2 gap-y-2"
                                        >
                                          <p className="text-muted-foreground">
                                            {subKey
                                              ?.replace(/_/g, ' ')
                                              ?.replace(/\b\w/g, (char) =>
                                                char?.toUpperCase(),
                                              )}
                                          </p>
                                          <div className="flex gap-2 text-muted-foreground font-semibold">
                                            :<p>{subValue || 'N/A'}</p>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={key}
                                className="grid grid-cols-2 gap-y-2"
                              >
                                <p className="text-muted-foreground">
                                  {key
                                    ?.replace(/_/g, ' ')
                                    ?.replace(/\b\w/g, (char) =>
                                      char?.toUpperCase(),
                                    )}
                                </p>
                                <div className="flex gap-2 text-muted-foreground font-semibold">
                                  :<p>{value || 'N/A'}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );

                    default:
                      return <p>Unknown Document Type</p>;
                  }
                })()}
              </div>
            </CardContent>
          </div>
        )}
      </div>
    </div>
  );
};
const TableRowForVoterId = ({ label, value }) => (
  <tr className="border break-avoid">
    <td className="p-2 bg-secondary  font-semibold border w-96">{label}</td>
    <td className="p-2 text-muted-foreground border w-96">{value}</td>
  </tr>
);

export default DocumentCard;
